import { useState, useEffect } from "react";
import { X, Download, Share2 } from "lucide-react";

export function AddToHomeScreen() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Don't show if already installed as standalone
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone === true) return;
    const dismissed = localStorage.getItem('a2hs_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    // Detect iOS (iPhone, iPad, iPod)
    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';
    const isiOS = /iPad|iPhone|iPod/.test(ua) ||
      (platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
      /Mac.*Safari/.test(ua) && navigator.maxTouchPoints > 0;
    setIsIOS(isiOS);

    if (isiOS) {
      const timer = setTimeout(() => setShow(true), 3000);
      return () => clearTimeout(timer);
    }

    // Android/Chrome — capture the beforeinstallprompt event
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShow(true), 3000);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Fallback: if no beforeinstallprompt fires after 5s on mobile, show generic prompt
    const isMobile = /Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    let fallbackTimer = null;
    if (isMobile) {
      fallbackTimer = setTimeout(() => {
        if (!deferredPrompt) setShow(true);
      }, 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [manualHint, setManualHint] = useState(false);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setShow(false);
      }
      setDeferredPrompt(null);
    } else {
      setManualHint(true);
    }
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem('a2hs_dismissed', Date.now().toString());
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm bg-zinc-900 text-white p-4 rounded-lg shadow-2xl z-[9990] animate-in slide-in-from-bottom duration-300"
      data-testid="a2hs-prompt"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1.5 text-zinc-400 hover:text-white"
        data-testid="a2hs-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3">
        <div className="p-2 bg-orange-600 rounded-lg flex-shrink-0">
          <Download className="w-5 h-5" />
        </div>
        <div className="flex-1 pr-4">
          <p className="text-sm font-bold mb-1">Install Pallet Cut Calculator</p>
          {isIOS ? (
            <div>
              <p className="text-xs text-zinc-400 leading-relaxed mb-2">
                Install this app on your iPhone for a full-screen experience:
              </p>
              <div className="space-y-1.5 text-xs">
                <div className="flex items-center gap-2 text-zinc-300">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-zinc-700 flex items-center justify-center text-[10px] font-bold">1</span>
                  Tap the <Share2 className="w-3.5 h-3.5 text-blue-400 inline flex-shrink-0" /> Share button below
                </div>
                <div className="flex items-center gap-2 text-zinc-300">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-zinc-700 flex items-center justify-center text-[10px] font-bold">2</span>
                  Scroll down and tap <span className="font-medium text-white">&quot;Add to Home Screen&quot;</span>
                </div>
                <div className="flex items-center gap-2 text-zinc-300">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-zinc-700 flex items-center justify-center text-[10px] font-bold">3</span>
                  Tap <span className="font-medium text-white">&quot;Add&quot;</span> to install
                </div>
              </div>
            </div>
          ) : (
            <>
              <p className="text-xs text-zinc-400 mb-3">Add to your home screen for quick access and a native app feel.</p>
              <button
                onClick={handleInstall}
                className="w-full py-2.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold rounded transition-colors"
                data-testid="a2hs-install-btn"
              >
                Install App
              </button>
              {manualHint && (
                <p className="text-[11px] text-zinc-300 mt-2">
                  Open your browser menu (⋮) and tap <span className="font-medium text-white">&quot;Install app&quot;</span> or <span className="font-medium text-white">&quot;Add to Home Screen&quot;</span>.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
